import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TbArrowLeft } from "react-icons/tb";
import { CustomButton, CustomModal, CustomLoader } from "../../../components";
import AxiosInstance from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/addon-configuration";
const PLANS_PATH = "/dashboard/masters/plans-pricing";
const ADDON_MODULE_LIST_API = "/saas/list/add-ons";
const ADDON_VIEW_API = "/saas/view/add-ons";

const isSuccess = (response) => response?.data?.status === true;

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const buildRangesFromSlab = (slab) =>
  [1, 2, 3, 4]
    .filter((n) => slab?.[`range${n}`] != null)
    .map((n) => ({ key: `range${n}`, upTo: slab[`range${n}`] }));

const ViewAddonConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [slab, setSlab] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const fetchAddonPricing = useCallback(async () => {
    setLoading(true);

    try {
      const viewRes = await AxiosInstance.post(ADDON_VIEW_API, {
        slab_configuration_id: Number(id),
      });

      if (!isSuccess(viewRes) || !viewRes.data?.data?.slab) {
        throw new Error(viewRes?.data?.message || "Failed to fetch add-on pricing");
      }

      const slabData = viewRes.data.data.slab;
      setSlab(slabData);
      setRanges(buildRangesFromSlab(slabData));

      // NOTE: the sample response only showed `slab`, no per-module
      // pricing, so this guesses the field name (`modules`, falling back to
      // `add_ons`). Confirm once you have a real response for a slab with
      // saved pricing.
      const existingModules = viewRes.data.data.modules || viewRes.data.data.add_ons || [];

      const moduleListRes = await AxiosInstance.post(ADDON_MODULE_LIST_API, {
        module_id: "",
        status: "",
        page: "",
        limit: "",
      });
      const catalog = isSuccess(moduleListRes) ? moduleListRes.data.data || [] : [];

      setPricingRows(
        existingModules.map((entry) => ({
          moduleId: entry.module_id,
          featureLabel:
            catalog.find((c) => c.id === entry.module_id)?.name ||
            `Module ${entry.module_id}`,
          range1: entry.price_range1 ?? "",
          range2: entry.price_range2 ?? "",
          range3: entry.price_range3 ?? "",
          range4: entry.price_range4 ?? "",
        })),
      );
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching add-on pricing",
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAddonPricing();
  }, [fetchAddonPricing]);

  if (loading) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading add-on pricing..." />
        </div>
      </div>
    );
  }

  if (!slab) {
    return (
      <div>
        <div className={styles.card}>Add-on pricing not found.</div>
        <CustomModal
          isOpen={statusModal.open}
          onClose={() => navigate(LIST_PATH)}
          title="Failed"
          size="sm"
        >
          <p className={styles.confirmText}>{statusModal.message}</p>
          <div className={styles.confirmActions}>
            <CustomButton
              variant="primary"
              fullWidth={false}
              className={styles.btnOrange}
              onClick={() => navigate(LIST_PATH)}
            >
              OK
            </CustomButton>
          </div>
        </CustomModal>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.breadcrumb}>
        <span className={styles.breadcrumbLink} onClick={() => navigate(PLANS_PATH)}>
          Plans &amp; Pricing
        </span>
        <span className={styles.breadcrumbSeparator}>&gt;</span>
        <span className={styles.breadcrumbLink} onClick={() => navigate(LIST_PATH)}>
          Add-on Configuration
        </span>
        <span className={styles.breadcrumbSeparator}>&gt;</span>
        <span className={styles.breadcrumbCurrent}>View Pricing</span>
      </div>

      <div className={styles.detailHeaderRow}>
        <div className={styles.detailHeaderLeft}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(LIST_PATH)}
          >
            <TbArrowLeft size={20} />
          </button>
          <h1 className={styles.detailTitle}>Add-on Pricing — Slab #{slab.id}</h1>
        </div>
        {slab.status !== "Archived" && (
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() =>
              navigate(`${LIST_PATH}/create`, { state: { slabId: slab.id } })
            }
          >
            Edit
          </CustomButton>
        )}
      </div>
      <p className={styles.detailDescription}>
        Add-on pricing configured for users up to {ranges[ranges.length - 1]?.upTo}.
      </p>

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Add-on Pricing by Module</h2>
        {pricingRows.length === 0 ? (
          <p style={{ color: "#6B7280" }}>No add-on pricing configured for this slab yet.</p>
        ) : (
          <div className={styles.matrixTableWrap}>
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th className={styles.matrixHeaderCell}>Plan Features</th>
                  {ranges.map((range) => (
                    <th key={range.key} className={styles.matrixHeaderCell}>
                      <span className={styles.matrixHeaderText}>
                        Up to {Number(range.upTo).toLocaleString()} users
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pricingRows.map((row) => (
                  <tr key={row.moduleId}>
                    <td className={styles.matrixRowLabel}>{row.featureLabel}</td>
                    {ranges.map((range) => (
                      <td key={range.key} className={styles.matrixCell}>
                        {formatCurrency(row[range.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewAddonConfig;
